import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, Send, X, Loader2, FileText, AlertOctagon, Target, ShieldAlert, Truck, Users, Wrench, Scale, Smile, ClipboardCheck, ListTodo, ExternalLink, LifeBuoy, Plus, Trash2, MessageSquare, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { api, getTenantScope, endpoints } from "@/lib/api";
import { toast } from "sonner";

const MODULE_ICON = {
  documents: FileText, ncrs: AlertOctagon, objectives: Target, risks: ShieldAlert,
  suppliers: Truck, employees: Users, trainings: Users, equipment: Wrench,
  legal: Scale, "legal-requirements": Scale, satisfaction: Smile, "customer-satisfaction": Smile,
  "audits-reviews": ClipboardCheck, "iso-tasks": ListTodo, assistance: LifeBuoy, system: Sparkles,
};

const ACTION_ICON = {
  create_task: ListTodo, create_ncr: AlertOctagon, open_url: ExternalLink, request_assistance: LifeBuoy,
};

const STARTER_QUESTIONS = [
  "When is our next internal audit?",
  "Which training certificates are expiring soon?",
  "Which equipment is overdue for calibration?",
  "What NCRs are still open?",
  "Show me our purchasing procedure.",
  "What should we prepare for our next Management Review?",
];

const SESSION_KEYS = "isoquest.ai.sessions"; // { [slug]: session_id }

function readSessions() { try { return JSON.parse(localStorage.getItem(SESSION_KEYS) || "{}"); } catch { return {}; } }
function writeSession(slug, sid) {
  const s = readSessions();
  if (sid) s[slug] = sid; else delete s[slug];
  localStorage.setItem(SESSION_KEYS, JSON.stringify(s));
}

/** Resolve the client_slug the user is currently working with. */
function useCurrentClientSlug() {
  const loc = useLocation();
  const [slug, setSlug] = useState(null);
  useEffect(() => {
    const path = loc.pathname || "";
    const m = path.match(/^\/clients\/([^/]+)/);
    if (m) return setSlug(m[1]);
    const q = new URLSearchParams(loc.search);
    const c = q.get("client");
    if (c) return setSlug(c);
    const sc = getTenantScope();
    if (sc?.slug && (sc.role === "client" || sc.role === "client_admin" || sc.role === "client_employee" || sc.role === "auditor")) {
      return setSlug(sc.slug);
    }
    setSlug(null);
  }, [loc]);
  return slug;
}

export default function AskIsoquestAI() {
  const slug = useCurrentClientSlug();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  // Load history when panel opens (per client)
  useEffect(() => {
    if (!open || !slug) return;
    const sid = readSessions()[slug];
    if (!sid) { setSessionId(null); setMessages([]); return; }
    setSessionId(sid);
    api.get(`/ai/history`, { params: { session_id: sid } })
      .then(({ data }) => setMessages((data.messages || []).map(m => ({ ...m }))))
      .catch(() => setMessages([]));
  }, [open, slug]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  if (!slug) return null;

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    const optimistic = [...messages, { role: "user", text: q, at: new Date().toISOString() }];
    setMessages(optimistic);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { client_slug: slug, message: q, session_id: sessionId });
      setSessionId(data.session_id);
      writeSession(slug, data.session_id);
      setMessages([...optimistic, {
        role: "assistant", text: data.answer,
        citations: data.citations || [], actions: data.suggested_actions || [],
        at: new Date().toISOString(),
      }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI is unavailable right now");
      setMessages(optimistic);
    } finally { setBusy(false); }
  };

  const resetSession = async () => {
    if (sessionId) {
      try { await api.delete("/ai/history", { params: { session_id: sessionId } }); } catch { /* ignore */ }
    }
    writeSession(slug, null);
    setSessionId(null);
    setMessages([]);
    toast.success("Conversation cleared");
  };

  const runAction = async (a) => {
    if (a.type === "open_url" && a.url) { navigate(a.url); setOpen(false); return; }
    if (a.type === "request_assistance") { navigate(`/assistance?client=${slug}`); setOpen(false); return; }
    if (a.type === "create_task") {
      const title = window.prompt("New task title", "");
      if (!title) return;
      try {
        await endpoints.createIsoTask({ client_slug: slug, title, description: `Suggested by ISOQuest AI` });
        toast.success("Task created");
      } catch (e) { toast.error(e.response?.data?.detail || "Could not create task"); }
      return;
    }
    if (a.type === "create_ncr") {
      navigate(`/ncrs?client=${slug}&new=1`);
      setOpen(false);
      return;
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        data-testid="ai-open-button"
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-5 z-40 group flex items-center gap-2 rounded-full pl-3 pr-4 py-3 shadow-2xl shadow-indigo-500/40 brand-gradient text-white hover:scale-[1.03] transition-transform"
        title="Ask ISOQuest AI"
      >
        <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-white/20 backdrop-blur">
          <Sparkles className="h-4.5 w-4.5" />
          <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-indigo-600 animate-pulse" />
        </span>
        <span className="text-sm font-semibold hidden sm:inline">Ask ISOQuest AI</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-stretch sm:justify-end">
          <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full sm:w-[440px] md:w-[520px] h-[88vh] sm:h-full bg-white dark:bg-slate-950 shadow-2xl border-l border-slate-200 dark:border-white/10 flex flex-col rounded-t-3xl sm:rounded-none">
            {/* Header */}
            <header className="px-4 py-3 border-b border-slate-200 dark:border-white/10 flex items-center gap-3 brand-gradient text-white">
              <div className="h-9 w-9 rounded-xl bg-white/20 flex items-center justify-center">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="flex-1 leading-tight">
                <div className="font-display font-extrabold text-[15px]">ISOQuest AI Consultant</div>
                <div className="text-[10px] uppercase tracking-widest opacity-80">Answering for <span className="font-semibold">{slug}</span></div>
              </div>
              <button data-testid="ai-reset" onClick={resetSession} className="p-1.5 rounded-md hover:bg-white/15" title="Clear conversation">
                <Trash2 className="h-4 w-4" />
              </button>
              <button data-testid="ai-close" onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-white/15" title="Close">
                <X className="h-4 w-4" />
              </button>
            </header>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
              {messages.length === 0 && (
                <div className="space-y-3">
                  <div className="rounded-2xl p-4 bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-white/10 shadow-sm">
                    <div className="text-sm">
                      Hi — I'm the <span className="brand-gradient-text font-semibold">ISOQuest AI Consultant</span>. Ask me anything about this client's ISO management system, or about ISO&nbsp;9001, 14001, 45001, 50001, 27001, 3834 or Medical Device QMS best practice.
                    </div>
                    <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-1.5">
                      <ShieldAlert className="h-3.5 w-3.5" /> I only ever look at <b>{slug}</b>'s data — never another client's.
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">Try one of these</div>
                    <div className="flex flex-wrap gap-2">
                      {STARTER_QUESTIONS.map((q) => (
                        <button
                          key={q}
                          data-testid={`ai-starter-${q.slice(0, 20).replace(/\s+/g, "-")}`}
                          onClick={() => ask(q)}
                          className="text-[12px] rounded-full border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800/70 px-3 py-1.5 hover:border-indigo-400 hover:text-indigo-700"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <MessageBubble key={i} m={m} onAction={runAction} slug={slug} />
              ))}

              {busy && (
                <div className="flex items-center gap-2 text-xs text-slate-500 pl-1">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> ISOQuest AI is thinking…
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="border-t border-slate-200 dark:border-white/10 p-3 bg-white dark:bg-slate-900">
              <div className="flex items-end gap-2">
                <Textarea
                  data-testid="ai-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
                  rows={2}
                  placeholder="Ask a question about this client's ISO system…"
                  className="flex-1 resize-none text-sm"
                />
                <Button data-testid="ai-send" onClick={() => ask()} disabled={busy || !input.trim()} className="brand-gradient text-white h-10 px-4">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
              <div className="mt-1.5 text-[10px] text-slate-400 flex items-center gap-1">
                <span>Enter to send · Shift+Enter for a new line</span>
                <span className="ml-auto">Powered by Claude Sonnet · scoped to {slug}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MessageBubble({ m, onAction, slug }) {
  const navigate = useNavigate();
  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-tr-sm brand-gradient text-white text-sm px-3.5 py-2 shadow-md">
          {m.text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="max-w-[92%] space-y-2">
        <div className="rounded-2xl rounded-tl-sm bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-white/10 text-sm p-3.5 shadow-sm whitespace-pre-wrap leading-relaxed">
          {m.text}
        </div>
        {m.citations && m.citations.length > 0 && (
          <div className="pl-1">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">Sources</div>
            <div className="flex flex-wrap gap-1.5">
              {m.citations.map((c, i) => {
                const Icon = MODULE_ICON[c.module] || FileText;
                const inner = (
                  <span className="inline-flex items-center gap-1.5 text-[11px] rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:border-indigo-500/30 dark:text-indigo-300 px-2.5 py-1">
                    <Icon className="h-3 w-3" /> {c.label}
                    {c.url && <ExternalLink className="h-3 w-3 opacity-60" />}
                  </span>
                );
                return c.url ? (
                  <button key={i} onClick={() => navigate(c.url)} className="hover:brightness-110">{inner}</button>
                ) : <span key={i}>{inner}</span>;
              })}
            </div>
          </div>
        )}
        {m.actions && m.actions.length > 0 && (
          <div className="pl-1 flex flex-wrap gap-1.5">
            {m.actions.map((a, i) => {
              const Icon = ACTION_ICON[a.type] || Sparkles;
              return (
                <button
                  key={i}
                  onClick={() => onAction(a)}
                  data-testid={`ai-action-${a.type}`}
                  className="inline-flex items-center gap-1.5 text-[11px] rounded-full brand-gradient text-white px-2.5 py-1 shadow hover:brightness-110"
                >
                  <Icon className="h-3 w-3" /> {a.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
